"use client";
import { useState } from "react";
import { ShieldHalf, Skull, Crosshair, ArrowRight, Radio, Lock } from "lucide-react";
import { useT } from "@/lib/i18n";

/**
 * Вход на арену (combat room): выбор команды RED/BLUE.
 * Статичный профессиональный брифинг — без декоративных анимаций.
 */
export function ArenaEntry({
  onEnter,
}: {
  onEnter: (side: "red" | "blue") => void;
}) {
  const t = useT();
  const [side, setSide] = useState<"red" | "blue" | null>(null);

  function enter() {
    if (side) onEnter(side);
  }

  return (
    <div className="hackentry" role="dialog" aria-modal="true">
      <div className="hackentry-inner">
        <header className="hackentry-top">
          <span className="hackentry-badge">
            <Radio size={13} /> {t("entry.badge")}
          </span>
          <span className="hackentry-sec">
            <Lock size={13} /> CyberAlem // COMBAT ROOM
          </span>
        </header>

        <div className="hackentry-boot" aria-hidden>
          {BOOT_LINES.map((l, i) => (
            <div key={i} className="hackentry-boot-line">
              {l}
            </div>
          ))}
        </div>

        <h1 className="hackentry-title">
          <span className="grad">{t("entry.title")}</span>
        </h1>
        <p className="hackentry-sub">{t("entry.sub")}</p>

        <div className="hackentry-teams">
          <TeamCard
            tone="red"
            icon={<Skull size={30} />}
            title="RED TEAM"
            role={t("entry.role_red")}
            desc={t("entry.desc_red")}
            goal={t("entry.goal_red")}
            selected={side === "red"}
            onSelect={() => setSide("red")}
          />
          <div className="hackentry-vs" aria-hidden>
            VS
          </div>
          <TeamCard
            tone="blue"
            icon={<ShieldHalf size={30} />}
            title="BLUE TEAM"
            role={t("entry.role_blue")}
            desc={t("entry.desc_blue")}
            goal={t("entry.goal_blue")}
            selected={side === "blue"}
            onSelect={() => setSide("blue")}
          />
        </div>

        <button
          type="button"
          className={`hackentry-enter ${side ? "ready" : ""}`}
          disabled={!side}
          onClick={enter}
        >
          <Crosshair size={18} />
          {side
            ? side === "red"
              ? t("entry.enter_red")
              : t("entry.enter_blue")
            : t("entry.choose")}
          <ArrowRight size={18} />
        </button>
        <p className="hackentry-note">{t("entry.note")}</p>
      </div>
    </div>
  );
}

function TeamCard({
  tone,
  icon,
  title,
  role,
  desc,
  goal,
  selected,
  onSelect,
}: {
  tone: "red" | "blue";
  icon: React.ReactNode;
  title: string;
  role: string;
  desc: string;
  goal: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={`teamcard teamcard-${tone} ${selected ? "selected" : ""}`}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <div className="teamcard-head">
        <span className="teamcard-icon">{icon}</span>
        <div>
          <h3>{title}</h3>
          <span className="teamcard-role">{role}</span>
        </div>
        <span className={`teamcard-check ${selected ? "on" : ""}`}>✓</span>
      </div>
      <p className="teamcard-desc">{desc}</p>
      <span className={`teamcard-goal ${tone}`}>{goal}</span>
    </button>
  );
}

/* Статичный статус изоляции (вывод системной диагностики — без анимации). */
const BOOT_LINES = [
  "range node: cyberalem-01 — OK",
  "egress NAT + egress-filter — OK",
  "target: shop-10.89.x.0/24 (internal) — UP",
  "workstation pool: kali · parrot · ubuntu — READY",
  "threat isolation: strict — ON",
];
